import { useMemo, useState } from "react";
import { Plus } from "lucide-react";

import { SanctionEventDetail } from "../../../api/sanctionEvents";
import { CreditFacility } from "../../../api/credit_accounts";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { ButtonV2 } from "../../../components/ui/button/v2";
import { useAppSelector } from "../../../app/hooks";
import {
    EnrichedSanctionEventDetail,
    enrichSanctionEventDetails,
    filterDetailsByFacilityCategory,
} from "../Logic/sanctionEventMappings";
import {
    CREDIT_FACILITY_SHORT_LABELS,
    FACILITY_CATEGORY_LABELS,
    FacilityCategory,
} from "../../creditAccounts/logic/creditAccountMappings";
import { getAccountProductInfoByAccountNumberMap } from "../../creditAccounts/logic/slice";
import { SanctionEventDetailsModal } from "./SanctionEventDetailsModal";
import { LAKH } from "../../../lib/utils";

const formatLakhs = (amount: number) => {
    const amountInLakhs = amount / LAKH;
    return Number.isInteger(amountInLakhs)
      ? String(amountInLakhs)
      : amountInLakhs.toFixed(2);
};

interface SanctionEventDetailsCellProps {
    category: FacilityCategory;
    details: SanctionEventDetail[];
    disabled?: boolean;
    getCreditFacilityForLoanAccount: (
      loanAccountNumber: string,
    ) => CreditFacility;
    loanAccountOptions: OptionNameValuePair<string>[];
    onApply: (details: SanctionEventDetail[]) => void;
}

export const SanctionEventDetailsCell: React.FC<
    SanctionEventDetailsCellProps
> = ({
    category,
    details,
    disabled = false,
    getCreditFacilityForLoanAccount,
    loanAccountOptions,
    onApply,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const accountByNumber = useAppSelector(getAccountProductInfoByAccountNumberMap);

    const categoryDetails = useMemo(
      () =>
        filterDetailsByFacilityCategory(
          enrichSanctionEventDetails(details, getCreditFacilityForLoanAccount),
          category,
        ),
      [category, details, getCreditFacilityForLoanAccount],
    );

    const getDisplayLabel = (detail: EnrichedSanctionEventDetail) =>
      CREDIT_FACILITY_SHORT_LABELS[detail.credit_facility];

    return (
      <div className="min-w-[190px] max-w-[240px] py-1">
        {categoryDetails.length > 0 ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsOpen(true)}
            className="w-full text-left disabled:cursor-not-allowed disabled:opacity-70"
          >
            <div className="flex flex-col gap-1">
              {categoryDetails.map((detail) => (
                <div
                  key={detail.raw.detail_id}
                  className="rounded border border-gray-200 bg-white px-2 py-1"
                >
                  <div className="truncate text-xs font-medium text-[#0A0A0A]">
                    {getDisplayLabel(detail)}
                  </div>
                  <div className="truncate text-xs text-[#737373]">
                    {accountByNumber[detail.raw.loan_account_number]?.ext_account_number || "--"}
                    {" · "}
                    {formatLakhs(detail.raw.sanction_amount)}L
                  </div>
                </div>
              ))}
            </div>
          </button>
        ) : (
          <div className="flex items-center">
            <ButtonV2
              variant="text"
              size="sm"
              disabled={disabled}
              prefix={<Plus className="w-4 h-4" />}
              className="px-0"
              onClick={() => setIsOpen(true)}
            >
              Add
            </ButtonV2>
          </div>
        )}

        {isOpen && (
          <SanctionEventDetailsModal
            title={`Credit Facility - ${FACILITY_CATEGORY_LABELS[category]}`}
            details={categoryDetails.map((detail) => detail.raw)}
            disabled={disabled}
            loanAccountOptions={loanAccountOptions}
            onClose={() => setIsOpen(false)}
            onApply={onApply}
          />
        )}
      </div>
    );
};
